import React from 'react'
import { CheckCircleIcon, XCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline'

export type B2bFacturaLineStatus = 'pending' | 'running' | 'ok' | 'skipped' | 'error'

export interface B2bFacturaLine {
  orderId: number
  orders_id: string
  remision: string
  status: B2bFacturaLineStatus
  detail?: string
}

interface B2bOfimaFacturaProgressModalProps {
  isOpen: boolean
  title?: string
  lines: B2bFacturaLine[]
  currentIndex: number
  isRunning: boolean
}

const statusLabel: Record<B2bFacturaLineStatus, string> = {
  pending: 'Pendiente',
  running: 'Facturando…',
  ok: 'Facturado',
  skipped: 'Ya tenía factura',
  error: 'Error'
}

const B2bOfimaFacturaProgressModal: React.FC<B2bOfimaFacturaProgressModalProps> = ({
  isOpen,
  title = 'Facturación B2B en OFIMA',
  lines,
  currentIndex,
  isRunning
}) => {
  if (!isOpen) return null

  const total = lines.length
  const finishedCount = lines.filter((l) =>
    l.status === 'ok' || l.status === 'skipped' || l.status === 'error'
  ).length
  const pct = total > 0 ? Math.round((finishedCount / total) * 100) : 0

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-gray-900/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="b2b-factura-modal-title"
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl">
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 id="b2b-factura-modal-title" className="text-lg font-semibold text-slate-900">
            {title}
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Pedidos B2B con remisión y sin factura FS. No cierre esta ventana hasta finalizar.
          </p>
        </div>

        <div className="max-h-[50vh] overflow-y-auto px-5 py-3">
          <ul className="space-y-2" aria-label="Progreso por pedido">
            {lines.map((line, idx) => (
              <li
                key={`${line.orderId}-${idx}`}
                className={`flex items-start gap-3 rounded-lg border px-3 py-2 text-sm ${
                  idx === currentIndex && line.status === 'running'
                    ? 'border-blue-300 bg-blue-50'
                    : 'border-slate-100 bg-slate-50'
                }`}
              >
                <span className="mt-0.5 shrink-0" aria-hidden>
                  {line.status === 'running' ? (
                    <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
                  ) : line.status === 'ok' ? (
                    <CheckCircleIcon className="h-5 w-5 text-emerald-600" />
                  ) : line.status === 'skipped' ? (
                    <CheckCircleIcon className="h-5 w-5 text-amber-500" />
                  ) : line.status === 'error' ? (
                    <XCircleIcon className="h-5 w-5 text-red-600" />
                  ) : (
                    <span className="inline-block h-5 w-5 rounded-full border-2 border-slate-300" />
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900">
                    {line.orders_id}
                    <span className="ml-2 font-mono text-xs text-slate-500">RE {line.remision}</span>
                  </div>
                  <div className="text-xs text-slate-600">{statusLabel[line.status]}</div>
                  {line.detail ? (
                    <div className="mt-1 break-words text-xs text-slate-700">{line.detail}</div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="border-t border-slate-200 px-5 py-4">
          <div className="mb-2 h-2 w-full overflow-hidden rounded-full bg-slate-200">
            <div
              className="h-2 rounded-full bg-blue-600 transition-all duration-300"
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-center text-sm text-slate-600">
            {isRunning
              ? `Procesando ${Math.min(currentIndex + 1, total)} de ${total}…`
              : `Completado (${finishedCount} de ${total})`}
          </p>
        </div>
      </div>
    </div>
  )
}

export default B2bOfimaFacturaProgressModal
